import type { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { Ref } from 'react';
import { forwardRef, useState } from 'react';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import type { WidgetRef } from '../../types';
import { dataSources } from '../../universal-widget/data-sources';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import { RiskBubbleChartSettingsModal } from './RiskBubbleChartSettingsModal';
import type { RiskBubbleChartSettings } from './riskBubbleChartSettingsSchema';
import { RiskBubbleChartView } from './RiskBubbleChartView';
import { useRiskBubbleChart } from './useRiskBubbleChart';

type Props = {
  controlType: RiskAssessmentResultControlType;
};

/**
 * The continuous counterpart of the risk heatmap (RSP-7291): under "Don't
 * round" a score falls between matrix cells, so each risk is plotted at its
 * exact position instead of being snapped to one. Registered per side in
 * `widgetPrivate.tsx`, like the heatmaps it replaces.
 */
export const RiskBubbleChart = forwardRef(
  (props: Props, ref: Ref<WidgetRef>) => {
    const [showModal, setShowModal] = useState(false);
    const [settings, setSettings] =
      useDashboardWidgetSettings<RiskBubbleChartSettings>();

    useWidgetSettingsHandler(ref, () => setShowModal(true));

    const {
      loading,
      points,
      options,
      emptyState,
      cluster,
      clusterCopy,
      openRisk,
      dismissCluster,
    } = useRiskBubbleChart({
      controlType: props.controlType,
      settings,
    });

    const handleSave = async (data: RiskBubbleChartSettings) => {
      setSettings(data);
    };

    return (
      <>
        {showModal && (
          <RiskBubbleChartSettingsModal
            onDismiss={() => setShowModal(false)}
            onSave={handleSave}
            dataSource={dataSources.risk}
          />
        )}
        <RiskBubbleChartView
          loading={loading}
          hasPoints={points.length > 0}
          options={options}
          emptyState={emptyState}
          cluster={cluster}
          clusterCopy={clusterCopy}
          onOpenRisk={openRisk}
          onDismissCluster={dismissCluster}
        />
      </>
    );
  }
);

RiskBubbleChart.displayName = 'RiskBubbleChart';
